/**
 * The route table: closed id unions, descriptor shapes, and one ordered,
 * frozen table of each. This is the ONLY module in the feature that may
 * contain a step path literal or a section id (ADR-20260826-vbpt, carried
 * forward) — section-state.ts, resume.ts and router.tsx consume the
 * StepId/SectionId types and the frozen tables this file exports, and
 * name no id or path of their own. See routing/README.md for the
 * documented heuristic that backs this claim.
 *
 * A step is a route (ADR-20260918-dd9e). A section carries a hostStepId,
 * never a path of its own — "navigate to the next unconfirmed section"
 * means "navigate to the step route that hosts it, opened to that
 * section," not "navigate to a route unique to that section."
 *
 * AC-5: adding a new capture section is exactly one new row in
 * SECTION_TABLE below. No route component and no resume-logic file
 * changes. See route-table.test.ts and resume.test.ts for the fixture
 * that proves this by constructing a supplemented table without editing
 * any other file.
 */

/**
 * The closed set of step ids. A step is a route: confirming details, the
 * change-check question, the exposures accordion, the declaration, and
 * the received confirmation. Confirm-details and change-check are two
 * separate routes (ADR-20261001-rjj8, TG-13) rather than the single
 * combined step they used to be. AC-3 precondition: every route parameter
 * value is drawn from this closed union, so no free string can become
 * one.
 */
export const STEP_IDS = [
  "confirm-details",
  "change-check",
  "exposures",
  "declaration",
  "received",
] as const;

export type StepId = (typeof STEP_IDS)[number];

/**
 * The closed set of section ids. A section is the unit that gets
 * confirmed, saved once, and restored or rendered empty on return. Every
 * section id is drawn from this union — AC-3's precondition again.
 */
export const SECTION_IDS = [
  "business-details",
  "change-check",
  "exposure-business-operations",
  "exposure-commercial-property",
  "exposure-commercial-auto",
  "exposure-commercial-watercraft",
  "exposure-general-liability",
  "exposure-liquor-liability",
  "exposure-marina-operators-legal-liability",
  "exposure-marine-employers-legal-liability",
  "exposure-transportation",
  "exposure-commercial-trucking",
  "exposure-workers-compensation",
  "exposure-inland-marine-equipment-floater",
  "exposure-claims-and-loss-history",
  "exposure-additional-coverage-considerations",
  "exposure-anything-else",
] as const;

export type SectionId = (typeof SECTION_IDS)[number];

/** The one SectionId this file's own predicates read as a scalar rather
 * than an opaque answers object — see toConfirmedAnswers below. Named
 * once, here, so no other module needs its own "change-check" literal. */
export const CHANGE_CHECK_SECTION_ID: SectionId = "change-check";

/**
 * TG-6a, ADR-20261015-h2s6: a section instance — the catalogue SectionId
 * plus the two optional facts (a policy number, an additional-details
 * page key) that distinguish one occurrence of a repeated or runtime-
 * inserted section from another on the SAME renewal. Declared here,
 * beside ConfirmedAnswers and SectionId, rather than in
 * state/section-instances.ts (the module that actually computes an
 * instance list): ConfirmedAnswers below re-keys on this type, and
 * route-table.ts is the one module `section-state.ts`/`resume.ts`/
 * `router.tsx` already consume for every shape of this kind
 * (ADR-20260826-vbpt's single-home rule). Putting it in
 * state/section-instances.ts instead would make routing/ depend on
 * state/ for its own declared ConfirmedAnswers shape while state/
 * independently depends on routing/ for SectionId and SECTION_TABLE —
 * a cycle `no-circular` (.dependency-cruiser.js) rejects outright.
 *
 * Deliberately a STRING superset of SectionId, not a disjoint branded
 * type or an object key: `SectionId` is assignable to
 * `SectionInstanceId` (every string literal in the SECTION_IDS union is
 * also a member of this wider union), so every existing ConfirmedAnswers
 * value, SectionStateEntry and section-save-lifecycle.ts call site —
 * none of which this task group edits — already supplies a bare
 * SectionId and keeps compiling and behaving identically: a section with
 * no repetition and no additional-details page (every section this
 * feature renders today) has an instance id byte-identical to its
 * SectionId. The two encoded suffixes widen it only for the cases the
 * closed SectionId union cannot express alone. `encodeSectionInstanceId`/
 * `decodeSectionInstanceId` below are the one place this encoding is
 * produced or read — no other module parses or builds this string by
 * hand (AC-14's "SectionInstanceId type exists carrying section type,
 * optional policy number and optional additional-details page key" is
 * satisfied by these two functions' round trip, not by hand-rolled
 * string concatenation elsewhere).
 */
export type SectionInstanceId = SectionId | `${SectionId}::${string}`;

/** The decoded fields a `SectionInstanceId` carries (AC-14). */
export interface SectionInstanceKey {
  readonly sectionId: SectionId;
  /** Present only when this instance is one of two-or-more repeated
   * occurrences of the same catalogue section (EXPOSURE-QUESTIONS.md:
   * "two policies of the same type"). */
  readonly policyNumber?: string;
  /** Present only when this instance IS a runtime-inserted
   * additional-details page, naming the repeat-block item that selected
   * it (EXPOSURE-QUESTIONS.md's "→ details page" convention). */
  readonly additionalDetailsPageKey?: string;
}

const SECTION_INSTANCE_SEPARATOR = "::";
const SECTION_INSTANCE_POLICY_TAG = "policy=";
const SECTION_INSTANCE_DETAILS_TAG = "details=";

/** Builds a `SectionInstanceId` from its decoded fields. Returns the bare
 * `sectionId` unchanged when neither optional field is supplied — the
 * single-instance common case stays byte-identical to the catalogue id
 * (see `SectionInstanceId`'s own doc comment). */
export function encodeSectionInstanceId(key: Readonly<SectionInstanceKey>): SectionInstanceId {
  const parts: string[] = [];
  if (key.policyNumber !== undefined) {
    parts.push(`${SECTION_INSTANCE_POLICY_TAG}${encodeURIComponent(key.policyNumber)}`);
  }
  if (key.additionalDetailsPageKey !== undefined) {
    parts.push(`${SECTION_INSTANCE_DETAILS_TAG}${encodeURIComponent(key.additionalDetailsPageKey)}`);
  }
  if (parts.length === 0) {
    return key.sectionId;
  }
  return `${key.sectionId}${SECTION_INSTANCE_SEPARATOR}${parts.join(SECTION_INSTANCE_SEPARATOR)}` as SectionInstanceId;
}

/** The inverse of `encodeSectionInstanceId` — round-trips every id that
 * function produces, including the bare-SectionId case. */
export function decodeSectionInstanceId(instanceId: SectionInstanceId): SectionInstanceKey {
  const [sectionId, ...parts] = instanceId.split(SECTION_INSTANCE_SEPARATOR);
  const key: { sectionId: SectionId; policyNumber?: string; additionalDetailsPageKey?: string } = {
    sectionId: sectionId as SectionId,
  };
  for (const part of parts) {
    if (part.startsWith(SECTION_INSTANCE_POLICY_TAG)) {
      key.policyNumber = decodeURIComponent(part.slice(SECTION_INSTANCE_POLICY_TAG.length));
    } else if (part.startsWith(SECTION_INSTANCE_DETAILS_TAG)) {
      key.additionalDetailsPageKey = decodeURIComponent(part.slice(SECTION_INSTANCE_DETAILS_TAG.length));
    }
  }
  return key;
}

/** The business-details section's id, named once here so the dedicated
 * confirm-details route (TG-15, ADR-20261001-rjj8) does not re-type the
 * literal itself (OBJ-139/FIND-204, TG-1's single-home rule). Mirrors
 * `CHANGE_CHECK_SECTION_ID`'s pattern for the dedicated change-check
 * route. */
export const BUSINESS_DETAILS_SECTION_ID: SectionId = "business-details";

/** The change-check section's own confirmed answer shape. */
export type ChangeCheckAnswer = "yes" | "no";

/**
 * Renewal-data facts an applicability predicate may consult (which lines
 * of business are actually renewing). Owned here as the minimal shape
 * TG-1 needs; the read path that populates it is a different feature's
 * concern (see product-spec.md's Out of Scope section).
 */
export interface RenewalData {
  readonly linesOfBusiness: readonly string[];
  /**
   * TG-4 (m1-exposure-detail-sourcing), ADR-20261020-bppm: every
   * `policiesInScope[].sectionIds` entry, flattened across every policy
   * (`toRenewalData`), in stored order. Server-computed from the
   * published section-line mapping artefact — `whenLineOfBusinessRenews`
   * below is this field's one reader. Replaces `linesOfBusiness` as the
   * basis for per-line section applicability; `linesOfBusiness` itself is
   * unchanged and kept for any other consumer that still wants the raw
   * Epic line codes.
   *
   * Optional, not a bare `readonly string[]`: the dozens of `RenewalData`
   * fixtures already built across this test suite for concerns unrelated
   * to per-line applicability (step-guard, resume, router, section-state,
   * the shared `renewal-capture-harness.tsx`) construct only
   * `{ linesOfBusiness }` literals. `whenLineOfBusinessRenews` treats a
   * missing value as `[]` (no per-line section applies) — the production
   * path (`toRenewalData`) always populates a real, possibly-empty array,
   * so this optionality is a test-fixture concession, not a production
   * ambiguity.
   */
  readonly sectionIds?: readonly string[];
}

/**
 * Confirmed section answers, keyed by section INSTANCE id (ADR-20261015-h2s6,
 * TG-6a — widened from SectionId). A key carrying a value means that instance
 * is confirmed; absence, or an explicit `undefined` value, means unconfirmed
 * — test confirmed-ness with `isSectionConfirmed` below, never `in` (per AC-2, no unconfirmed value is
 * ever persisted anywhere, including here). Values are opaque to the
 * routing layer — a section's own capture component owns their shape, with
 * the single named exception of the change-check answer, which this file's
 * isApplicable predicates read directly.
 *
 * Because SectionInstanceId is a superset of SectionId (its own doc
 * comment above), this widening is additive: every existing caller that
 * builds or reads a ConfirmedAnswers value with a bare SectionId key keeps
 * compiling and behaving identically — section-save-lifecycle.ts and every
 * route component this task group does not touch never needed to change.
 */
export type ConfirmedAnswers = Readonly<Partial<Record<SectionInstanceId, unknown>>>;

/** The one confirmed-ness check (DEBT-20261007-fx00): `!== undefined`, never key presence
 * (`in`), so a key present with an explicit `undefined` value reads as unconfirmed everywhere. */
export function isSectionConfirmed(confirmedAnswers: ConfirmedAnswers, sectionId: SectionInstanceId): boolean {
  return confirmedAnswers[sectionId] !== undefined;
}

/**
 * A section's applicability predicate. ADR-20260918-n2bf: one additional
 * parameter over a plain predicate-over-renewal-data, not a redefinition
 * of renewalData itself. Never a literal true/false (AC-7's repository
 * check).
 */
export type IsApplicable = (
  renewalData: RenewalData,
  confirmedAnswers: ConfirmedAnswers,
) => boolean;

/**
 * Declares that a repeat-block field's chosen outcome, for one or more
 * items, inserts a runtime additional-details page after this section
 * (EXPOSURE-QUESTIONS.md's "→ details page" convention — TG-6a,
 * ADR-20261015-h2s6). `expandSectionInstances` (state/section-instances.ts)
 * is the one function that reads this declaratively — no per-section code
 * exists there, so adding a new repeat-block trigger is still exactly one
 * SECTION_TABLE row (AC-5).
 *
 * The repeat-block field's own saved answer (an opaque value to every
 * OTHER consumer of ConfirmedAnswers, per this file's header comment) is
 * read here as `readonly { key: string; outcome: string }[]` — one entry
 * per item the customer has added to that repeat block, `key` a stable
 * per-item identifier and `outcome` the radio choice that item recorded
 * (e.g. "Purchased"). This shape, like the line-of-business codes
 * `whenLineOfBusinessRenews` reads, is this feature's own placeholder
 * mechanism pending the real repeat-block capture UI — see this file's
 * existing caveat on `whenLineOfBusinessRenews` for the identical
 * reasoning.
 */
export interface AdditionalDetailsRule {
  readonly fieldId: string;
  readonly triggerOutcomes: readonly string[];
}

/**
 * One row of the section table. `fields` names the capture fields this
 * section owns (content only — no field value ever appears here).
 * `hostStepId` is the section's route identity (ADR-20260918-dd9e): a
 * section carries no path of its own.
 *
 * `lineOfBusinessCode` and `additionalDetailsRules` are TG-6a additions
 * (ADR-20261015-h2s6), both optional and both read only by
 * `expandSectionInstances` — no existing consumer of SECTION_TABLE reads
 * either field, so this addition changes no other module's behaviour.
 */
export interface SectionDescriptor {
  readonly id: SectionId;
  readonly hostStepId: StepId;
  readonly order: number;
  readonly labelKey: string;
  readonly fields: readonly string[];
  readonly isApplicable: IsApplicable;
  /** Present only for a section conditional on a specific line of
   * business — the same code `whenLineOfBusinessRenews` below closes
   * over for `isApplicable`, stamped alongside it by the `perLine` helper
   * so the two can never drift apart (one call site produces both).
   * `expandSectionInstances` reads this to decide how many policies this
   * section type repeats for; `isApplicable` itself stays the single
   * source of truth for whether the section type appears at all. */
  readonly lineOfBusinessCode?: string;
  /** Present only for a section with at least one repeat-block field that
   * can trigger an additional-details page. Declarative data read only by
   * `expandSectionInstances` (AC-5: still one row per section type). */
  readonly additionalDetailsRules?: readonly AdditionalDetailsRule[];
}

/** One row of the step table. The only place a step path literal lives. */
export interface StepDescriptor {
  readonly id: StepId;
  readonly path: string;
  readonly order: number;
  /**
   * ADR-20261016-q2mt (TG-6): true only for the step a "Save and finish
   * later" navigation always targets with `?outcome=saved` (`received`).
   * Declared here, as data, rather than as a step-id literal inside
   * `router.tsx`'s guard loader (ADR-20260826-vbpt), so a request for
   * `<any other step>?outcome=saved` is never silently let through —
   * the query-string exception only ever applies to the one step this
   * field names `true` for.
   */
  readonly allowsSaveOutcomeOverride?: boolean;
  /**
   * ADR-20261016-y2kd (TG-8): true for "exposures" and "received" — the
   * two steps a contributor session may reach directly (from the
   * contributor Start control, and from its own delegated section
   * instances) without having settled the OWNER's earlier steps in
   * `STEP_TABLE` order. A contributor's progress is tracked separately
   * from `sectionStates`/`resolveResumeTarget`'s owner-only model (see
   * `StepGuardContext.isContributorSession`, router.tsx), so the ordinary
   * order computation has nothing meaningful to say about where a
   * contributor "is" on either step. Declared here, as data, for the same
   * reason `allowsSaveOutcomeOverride` is: so the exemption only ever
   * applies to the step(s) this field names `true` for, never silently to
   * every step. "confirm-details" and "declaration" stay owner-only —
   * both already render their own not-permitted view for a contributor
   * independently of the guard, and the server's 403 outside the
   * delegated set is the actual enforcement; this field only controls
   * whether the CLIENT-side order guard redirects a contributor away
   * before that view or that 403 is ever reached.
   */
  readonly allowsContributorStepOrderExemption?: boolean;
}

/** Freezes a descriptor and every array it holds, so a frozen table's
 * entries can't be mutated in place even though `readonly` alone is only
 * a compile-time guarantee. */
function freezeSection(section: SectionDescriptor): SectionDescriptor {
  Object.freeze(section.fields);
  return Object.freeze(section);
}

function freezeStep(step: StepDescriptor): StepDescriptor {
  return Object.freeze(step);
}

const UNFROZEN_STEP_TABLE: readonly StepDescriptor[] = [
  { id: "confirm-details", path: "/renewal/confirm-details", order: 1 },
  { id: "change-check", path: "/renewal/change-check", order: 2 },
  { id: "exposures", path: "/renewal/exposures", order: 3, allowsContributorStepOrderExemption: true },
  { id: "declaration", path: "/renewal/declaration", order: 4 },
  {
    id: "received",
    path: "/renewal/received",
    order: 5,
    allowsSaveOutcomeOverride: true,
    allowsContributorStepOrderExemption: true,
  },
];

export const STEP_TABLE: readonly StepDescriptor[] = Object.freeze(UNFROZEN_STEP_TABLE.map(freezeStep));

/**
 * The closed set of stepper stage ids (ADR-20261001-rz8p, TG-17). The
 * customer-facing landing-route stepper shows three stages even though
 * `STEP_TABLE` has five routes (TG-13's confirm-details/change-check
 * split): the first stage spans two routes, the other two map
 * one-to-one. `received` carries no stage -- it is the post-submission
 * confirmation screen, not a step the stepper tracks progress toward.
 */
export const STEPPER_STAGE_IDS = ["confirm-details", "exposures", "declaration"] as const;

export type StepperStageId = (typeof STEPPER_STAGE_IDS)[number];

/**
 * One row of the stepper stage table. `stepIds` names every `StepId`
 * (drawn only from `STEP_IDS` above) this stage reports settlement for.
 * This is the one place the stage-to-step grouping is declared
 * (ADR-20261001-rz8p: "computed from STEP_TABLE/SECTION_TABLE, never a
 * second hand-maintained list") -- `section-state.ts`'s
 * `computeStepperStages` consumes this table and contains no step id
 * literal of its own.
 */
export interface StepperStageDescriptor {
  readonly id: StepperStageId;
  readonly order: number;
  readonly labelKey: string;
  readonly stepIds: readonly StepId[];
}

const UNFROZEN_STEPPER_STAGE_TABLE: readonly StepperStageDescriptor[] = [
  {
    id: "confirm-details",
    order: 1,
    labelKey: "routing.stage.confirmDetails.label",
    stepIds: ["confirm-details", "change-check"],
  },
  {
    id: "exposures",
    order: 2,
    labelKey: "routing.stage.reviewExposures.label",
    stepIds: ["exposures"],
  },
  {
    id: "declaration",
    order: 3,
    labelKey: "routing.stage.declaration.label",
    stepIds: ["declaration"],
  },
];

function freezeStepperStage(stage: StepperStageDescriptor): StepperStageDescriptor {
  Object.freeze(stage.stepIds);
  return Object.freeze(stage);
}

export const STEPPER_STAGE_TABLE: readonly StepperStageDescriptor[] = Object.freeze(
  UNFROZEN_STEPPER_STAGE_TABLE.map(freezeStepperStage),
);

/**
 * TG-5/AC-13 (m1-magic-link-portal-refresh): the H1 "Has anything
 * changed?" fast path is gone — "every customer goes through the
 * sections" (H2 README, Screen 3). This always returns `true` now; the
 * change-check section itself is retired from the applicable set below
 * (its own `isApplicable` is now `() => false`), so
 * `CHANGE_CHECK_SECTION_ID`/`ChangeCheckAnswer` are kept only for
 * `toConfirmedAnswers`'s historical-data bridge (a renewal confirmed under
 * the old flow may still carry a stored answer) — nothing writes a new one
 * going forward. Kept as a named function, not inlined at each call site,
 * so the retired rule's own history stays in one place. */
function changeCheckAllowsExposures(): boolean {
  return true;
}

/**
 * Bridges WorkingCopyPort's stored shape (a plain-string sectionId and an
 * opaque answers record) into the ConfirmedAnswers map this file's own
 * isApplicable predicates and changeCheckAllowsExposures consume. Every
 * section's stored answers stay opaque here — the single named exception
 * is CHANGE_CHECK_SECTION_ID, because changeCheckAllowsExposures reads
 * that entry directly as the scalar ChangeCheckAnswer, not as an answers
 * object.
 *
 * FIND-41/OBJ-26: this bridge — and the "change-check" literal it needs —
 * used to live in RenewalLanding.tsx, a second production module holding
 * a section id in violation of this file's own single-home rule (this
 * file's header comment, routing/README.md). It was independently
 * duplicated again in the test harness, so the two could drift apart
 * (a typo in one, not the other) with every gate green. Moved here, the
 * one module allowed to name a section id, and exported so
 * RenewalLanding.tsx and the harness both consume this single copy
 * instead of holding their own.
 */
export function toConfirmedAnswers(
  confirmedSections: readonly { sectionId: string; answers: Record<string, unknown> }[],
): ConfirmedAnswers {
  const result: Partial<Record<string, unknown>> = {};
  for (const section of confirmedSections) {
    result[section.sectionId] =
      section.sectionId === CHANGE_CHECK_SECTION_ID
        ? section.answers["hasAnythingChanged"]
        : section.answers;
  }
  return result as ConfirmedAnswers;
}

/**
 * OBJ-98/FIND-121: the single conversion from a renewal summary's
 * policies-in-scope list to the `RenewalData` applicability predicates
 * read. Before this helper, App.tsx built the identical
 * `.map((policy) => policy.lineOfBusiness)` twice independently — once
 * for the rendered step (`useCaptureState`), once for the route guard
 * (`loadGuardContext`) — plus further independent copies in
 * RenewalLanding.tsx and the integration-test harness. Only the guard's
 * copy decides AC-8 (whether an out-of-order step redirects), so a drift
 * between it and the rendered step's copy would let the two disagree
 * about which sections are applicable with nothing to catch it. One
 * exported function, consumed by every site that needs this conversion,
 * closes that drift risk structurally rather than by convention.
 */
/**
 * TG-4 (ADR-20261016-xfwy): reads `lineCodes` (one policy may now carry
 * more than one line), flattened across every policy in stored order --
 * not `lineOfBusiness`, the single-line-per-policy field this amendment
 * removes from the wire contract entirely.
 *
 * m1-exposure-detail-sourcing TG-4 (ADR-20261020-bppm) additionally
 * flattens `sectionIds` the same way -- optional per policy, so a policy
 * with none contributes nothing (`?? []`), never `undefined` entries.
 */
export function toRenewalData(
  policiesInScope: readonly { readonly lineCodes: readonly string[]; readonly sectionIds?: readonly string[] }[],
): RenewalData {
  return {
    linesOfBusiness: policiesInScope.flatMap((policy) => policy.lineCodes),
    sectionIds: policiesInScope.flatMap((policy) => policy.sectionIds ?? []),
  };
}

/** An exposure section that populates for every client (EXPOSURE-
 * QUESTIONS.md: "populate for every client") — TG-5/AC-13 retired the
 * change-check gate this predicate used to also consult, so it is now
 * unconditional. Not a literal true/false (AC-7's repository check) —
 * still a predicate function, declared with fewer parameters than
 * `IsApplicable` names (a function assignable to that type may ignore
 * trailing parameters it does not need), evaluated per renewal. */
function alwaysApplicable(): boolean {
  return changeCheckAllowsExposures();
}

/**
 * An exposure section conditional on a line of business actually renewing
 * (EXPOSURE-QUESTIONS.md: "conditional on the client having that policy
 * renewing at the time").
 *
 * m1-exposure-detail-sourcing TG-4 (ADR-20261020-bppm): `code` is no
 * longer an arbitrary client-side line-of-business slug matched against
 * raw Epic line codes (`linesOfBusiness`) — that mechanism was always a
 * placeholder, disconnected from any real Epic code (this function's own
 * prior doc comment named it so). `code` is now the section's own
 * `SectionId` (`perLine`'s caller already names it), matched against
 * `renewalData.sectionIds` — the server-computed set from
 * `policiesInScope[].sectionIds` (summary 2.1.0), itself derived from the
 * published section-line mapping artefact against the real Epic line
 * codes. A renewal with no `sectionIds` anywhere (every policy predates
 * the mapping, or matches no mapping entry) renews no per-line section at
 * all — only the four always-applicable sections remain.
 */
function whenLineOfBusinessRenews(sectionId: SectionId) {
  return (renewalData: RenewalData): boolean =>
    changeCheckAllowsExposures() && (renewalData.sectionIds ?? []).includes(sectionId);
}

/**
 * TG-6a (ADR-20261015-h2s6): produces the pair of fields a per-line
 * SECTION_TABLE row needs — `isApplicable` (the existing predicate) and
 * `lineOfBusinessCode` (the same code, read declaratively by
 * `expandSectionInstances` for per-policy fan-out) — from one call site,
 * so the two can never independently drift to name different codes.
 * Spread into a row literal: `{ ...perLine("exposure-commercial-property"), ... }`.
 *
 * m1-exposure-detail-sourcing TG-4: `code` is now the row's OWN `id`
 * (passed verbatim at each call site below, immediately after `id:` in
 * the same object literal) rather than an independent slug — both fields
 * this helper stamps are therefore keyed on the one identifier
 * `policiesInScope[].sectionIds` (summary 2.1.0) already names.
 */
function perLine(code: SectionId): { isApplicable: IsApplicable; lineOfBusinessCode: string } {
  return { isApplicable: whenLineOfBusinessRenews(code), lineOfBusinessCode: code };
}

const UNFROZEN_SECTION_TABLE: readonly SectionDescriptor[] = [
    {
      id: "business-details",
      hostStepId: "confirm-details",
      order: 1,
      labelKey: "routing.section.businessDetails.label",
      // OBJ-139/FIND-204, resolved: kept in step with
      // ConfirmDetailsRoute.tsx's own FIELDS -- that dedicated route
      // (TG-15) is this section's only renderer. Verified equal (order
      // irrelevant) by ConfirmDetailsRoute.test.tsx's cross-check test, so
      // this exact drift cannot recur silently.
      fields: ["businessName", "mailingAddress", "contactName", "contactPhone", "contactEmail"],
      isApplicable: (): boolean => true,
    },
    {
      // TG-5/AC-13: retired -- never applicable, so step-guard.ts's own
      // "every hosted section inapplicable" rule redirects any request for
      // this step's route to the earliest incomplete step, and
      // section-state.ts settles "confirm-details" as soon as
      // business-details alone is confirmed. The descriptor itself stays
      // (rather than being deleted outright) so STEP_TABLE's
      // "change-check" route entry still has a hosted section to evaluate
      // against -- deleting both together is a larger routing-table
      // reshape this task group's own scope does not extend to.
      id: "change-check",
      hostStepId: "change-check",
      order: 1,
      labelKey: "routing.section.changeCheck.label",
      fields: ["hasAnythingChanged"],
      isApplicable: (): boolean => false,
    },
    {
      id: "exposure-business-operations",
      hostStepId: "exposures",
      order: 1,
      labelKey: "routing.section.exposureBusinessOperations.label",
      fields: [
        "operationsChanged",
        "locationsAddedOrDiscontinued",
        "newConstruction",
        "ownershipOrManagementChanged",
        "mergersAcquisitionsDivestitures",
        "estimatedAnnualGrossRevenues",
      ],
      isApplicable: alwaysApplicable,
    },
    {
      id: "exposure-commercial-property",
      hostStepId: "exposures",
      order: 2,
      labelKey: "routing.section.exposureCommercialProperty.label",
      fields: ["buildingsChanged", "buildingSystemsImproved", "occupancyChanged", "limitsChanged"],
      ...perLine("exposure-commercial-property"),
      // EXPOSURE-QUESTIONS.md §2 Q1: "→ details page for each Purchased
      // building" -- an item change of "Sold"/"Renovated"/"Expanded" never
      // triggers one.
      additionalDetailsRules: [{ fieldId: "buildingsChanged", triggerOutcomes: ["Purchased"] }],
    },
    {
      id: "exposure-commercial-auto",
      hostStepId: "exposures",
      order: 3,
      labelKey: "routing.section.exposureCommercialAuto.label",
      fields: ["vehiclesChanged", "driversChanged", "personalVehiclesUsed", "afterMarketModifications"],
      ...perLine("exposure-commercial-auto"),
      // EXPOSURE-QUESTIONS.md §3 Q1: "→ details page for each
      // Purchased / Replaced", never for "Sold".
      additionalDetailsRules: [{ fieldId: "vehiclesChanged", triggerOutcomes: ["Purchased", "Replaced"] }],
    },
    {
      id: "exposure-commercial-watercraft",
      hostStepId: "exposures",
      order: 4,
      labelKey: "routing.section.exposureCommercialWatercraft.label",
      fields: ["watercraftChanged", "operatorsChanged"],
      ...perLine("exposure-commercial-watercraft"),
      // EXPOSURE-QUESTIONS.md §4 Q1: "same shape as Commercial auto Q1".
      additionalDetailsRules: [{ fieldId: "watercraftChanged", triggerOutcomes: ["Purchased", "Replaced"] }],
    },
    {
      id: "exposure-general-liability",
      hostStepId: "exposures",
      order: 5,
      labelKey: "routing.section.exposureGeneralLiability.label",
      fields: ["grossSalesChanged", "newProductsOrServices", "newContracts", "subcontractsWork"],
      ...perLine("exposure-general-liability"),
    },
    {
      id: "exposure-liquor-liability",
      hostStepId: "exposures",
      order: 6,
      labelKey: "routing.section.exposureLiquorLiability.label",
      fields: ["grossSalesChanged"],
      ...perLine("exposure-liquor-liability"),
    },
    {
      id: "exposure-marina-operators-legal-liability",
      hostStepId: "exposures",
      order: 7,
      labelKey: "routing.section.exposureMarinaOperatorsLegalLiability.label",
      fields: ["grossSalesChanged"],
      ...perLine("exposure-marina-operators-legal-liability"),
    },
    {
      id: "exposure-marine-employers-legal-liability",
      hostStepId: "exposures",
      order: 8,
      labelKey: "routing.section.exposureMarineEmployersLegalLiability.label",
      fields: ["grossSalesChanged"],
      ...perLine("exposure-marine-employers-legal-liability"),
    },
    {
      id: "exposure-transportation",
      hostStepId: "exposures",
      order: 9,
      labelKey: "routing.section.exposureTransportation.label",
      fields: ["grossSalesChanged", "unitCountsChanged", "vehiclesChanged", "driversChanged"],
      ...perLine("exposure-transportation"),
      // H2 trans-vehicles: a details page for each purchased or replaced vehicle.
      additionalDetailsRules: [{ fieldId: "vehiclesChanged", triggerOutcomes: ["Purchased", "Replaced"] }],
    },
    {
      id: "exposure-commercial-trucking",
      hostStepId: "exposures",
      order: 10,
      labelKey: "routing.section.exposureCommercialTrucking.label",
      fields: ["grossSalesChanged", "commoditiesChanged", "fleetChanged", "driversChanged"],
      ...perLine("exposure-commercial-trucking"),
      // H2 truck-vehicles: a details page for each purchased or replaced vehicle.
      additionalDetailsRules: [{ fieldId: "fleetChanged", triggerOutcomes: ["Purchased", "Replaced"] }],
    },
    {
      id: "exposure-workers-compensation",
      hostStepId: "exposures",
      order: 11,
      labelKey: "routing.section.exposureWorkersCompensation.label",
      fields: ["payrollChanged", "remoteEmployeesChanged", "employeeCountChanged"],
      ...perLine("exposure-workers-compensation"),
    },
    {
      id: "exposure-inland-marine-equipment-floater",
      hostStepId: "exposures",
      order: 12,
      labelKey: "routing.section.exposureInlandMarineEquipmentFloater.label",
      fields: ["equipmentChanged", "equipmentLeased"],
      ...perLine("exposure-inland-marine-equipment-floater"),
      // H2 im-equipment: a details page for each purchased or replaced piece of equipment.
      additionalDetailsRules: [{ fieldId: "equipmentChanged", triggerOutcomes: ["Purchased", "Replaced"] }],
    },
    {
      id: "exposure-claims-and-loss-history",
      hostStepId: "exposures",
      order: 13,
      labelKey: "routing.section.exposureClaimsAndLossHistory.label",
      fields: ["newClaims", "lossHistoryChanged"],
      isApplicable: alwaysApplicable,
    },
    {
      id: "exposure-additional-coverage-considerations",
      hostStepId: "exposures",
      order: 14,
      labelKey: "routing.section.exposureAdditionalCoverageConsiderations.label",
      fields: ["additionalCoverageNeeded"],
      isApplicable: alwaysApplicable,
    },
    {
      id: "exposure-anything-else",
      hostStepId: "exposures",
      order: 15,
      labelKey: "routing.section.exposureAnythingElse.label",
      fields: ["anythingElse"],
      isApplicable: alwaysApplicable,
    },
];

export const SECTION_TABLE: readonly SectionDescriptor[] = Object.freeze(
  UNFROZEN_SECTION_TABLE.map(freezeSection),
);
